<script setup lang="ts">
import type { IntRange } from 'type-fest'

const { t, tm, rt } = useI18n()

useHead({
  link: [{
    rel: 'canonical',
    href: `${import.meta.env.VITE_MONOLITH_URL}/how-it-works`,
  }],
})

useSeoMeta({
  title: t('how_it_works.meta.title'),
  description: t('how_it_works.meta.description'),
  ogTitle: t('how_it_works.meta.title'),
  ogDescription: t('how_it_works.meta.description'),
  ogImage: `${S3_ASSETS_URL}/meta-default.jpg`,
})

function getStaticAssets(index: StringifiedInt<IntRange<0, 6>>) {
  const baseUrl = `${S3_ASSETS_URL}/how-it-works`

  return match(index)
    .with('0', () => ({
      background: `${baseUrl}/customers/background.jpg`,
      foreground: `${baseUrl}/customers/foreground.png`,
    }))
    .with('1', () => ({
      background: `${baseUrl}/engagement/background.jpg`,
      foreground: `${baseUrl}/engagement/foreground.png`,
    }))
    .with('2', () => ({
      background: `${baseUrl}/bookings/background.jpg`,
      foreground: `${baseUrl}/bookings/foreground.png`,
    }))
    .with('3', () => ({
      background: `${baseUrl}/discovery/background.jpg`,
      foreground: `${baseUrl}/discovery/foreground.png`,
    }))
    .with('4', () => ({
      background: `${baseUrl}/fullness/background.jpg`,
      foreground: `${baseUrl}/fullness/foreground.png`,
    }))
    .with('5', () => ({
      background: `${baseUrl}/roi/background.jpg`,
      foreground: `${baseUrl}/roi/foreground.png`,
    }))
    .exhaustive()
}
</script>

<template>
  <div class="isolate">
    <LayoutNavbar />

    <section class="mb-8 grid gap-y-20 bg-gradient-to-b from-[#F8F8F0] to-[#FDFDFB] px-4 pt-10 shadow-2xl shadow-black/10 lg:mb-16 lg:rounded-b-[5rem] lg:px-8 lg:py-10">
      <header class="mx-auto grid max-w-screen-lg place-items-center gap-y-8 text-center">
        <h1 class="mx-auto max-w-screen-sm text-3xl font-medium !leading-tight lg:text-5xl">
          {{ t('how_it_works.title') }}
        </h1>

        <p class="text-lg text-neutral-600 lg:text-xl">
          {{ t('how_it_works.description') }}
        </p>

        <UiButton
          type="button"
          color="accent"
          class="group"
          as-child
        >
          <RouterLink :to="{ name: '/free-demo'}">
            {{ t('how_it_works.action') }}

            <UiIcon
              name="arrow-forward-rounded"
              class="transition-transform duration-200 group-hover:translate-x-1"
            />
          </RouterLink>
        </UiButton>
      </header>

      <div class="isolate mx-auto max-w-screen-xl [counter-reset:timeline]">
        <div
          v-for="(timelineItem, timelineIndex) in tm('how_it_works.timeline')"
          :key="timelineIndex"
          class="grid grid-cols-[min-content_1fr] place-items-center gap-5 lg:grid-cols-[1fr_min-content_1fr] lg:px-4"
        >
          <div
            :class="['grid w-full gap-y-5 pb-4 pt-10 lg:py-14', {
              'lg:order-last': Number(timelineIndex) % 2 !== 0
            }]"
          >
            <h3 class="text-balance text-3xl font-medium">
              {{ rt(timelineItem.title) }}
            </h3>

            <p class="text-neutral-600">
              {{ rt(timelineItem.description) }}
            </p>

            <ul class="grid gap-y-2 font-medium">
              <li
                v-for="(listItem, listIndex) in tm(`how_it_works.timeline.${timelineIndex}.items`)"
                :key="listIndex"
                class="flex items-center gap-x-2"
              >
                <span class="rounded-[50%] bg-orange-500/10 p-1 text-sm text-orange-500">
                  <UiIcon name="tick-solid" />
                </span>
                {{ rt(listItem) }}
              </li>
            </ul>

            <div
              class="rounded-lg bg-orange-500/10 p-2 text-orange-700/90"
              role="alert"
            >
              <span class="font-bold">Result: </span>
              {{ rt(timelineItem.note) }}
            </div>
          </div>

          <div class="relative order-first row-span-2 size-full before:inline-flex before:size-10 before:items-center before:justify-center before:rounded-[50%] before:bg-orange-500 before:text-xl before:font-semibold before:tabular-nums before:text-white before:ring-4 before:ring-orange-400/30 before:content-[counter(timeline)] before:[counter-increment:timeline] after:absolute after:left-1/2 after:z-[-1] after:h-full after:-translate-x-1/2 after:border-r after:border-dashed after:border-baltic-200 lg:order-none lg:row-span-1" />

          <div
            :class="['relative col-start-2 mb-10 flex size-full max-h-96 justify-center overflow-clip rounded-[1.875rem] bg-cover p-4 lg:col-start-auto lg:mb-0', {
              'lg:order-first': Number(timelineIndex) % 2 !== 0,
              'items-center': ['0', '3', '4'].includes(timelineIndex),
              'items-end !pb-0': ['1', '2', '3', '5'].includes(timelineIndex),
              'bg-bottom': timelineIndex === '3'
            }]"
            :style="{
              backgroundImage: `url(${getStaticAssets(timelineIndex).background})`
            }"
          >
            <img
              :src="getStaticAssets(timelineIndex).foreground"
              :class="['object-contain', {
                'h-3/4': ['0', '3', '4'].includes(timelineIndex),
                'h-5/6': ['1', '2', '3', '5'].includes(timelineIndex)
              }]"
            >
          </div>
        </div>
      </div>

      <section class="-mx-4 max-w-screen-xl lg:mx-auto lg:w-full lg:px-4 lg:pb-8">
        <div class="relative overflow-clip px-8 py-12 lg:rounded-3xl">
          <div class="absolute inset-0 isolate before:absolute before:inset-0 before:z-[1] before:bg-gray-800/70">
            <img
              :src="`${S3_ASSETS_URL}/home/cta-02.jpg`"
              class="aspect-video size-full object-cover object-center"
              role="presentation"
            >
          </div>

          <div class="isolate mx-auto grid max-w-screen-md place-items-center gap-y-8">
            <h3 class="text-balance text-center text-3xl leading-tight text-white lg:text-5xl">
              {{ t('how_it_works.cta_banner.title') }}
            </h3>

            <p class="text-balance text-center text-lg leading-tight text-white lg:text-xl">
              {{ t('how_it_works.cta_banner.description') }}
            </p>

            <UiButton
              class="group w-full max-w-xs lg:max-w-max"
              as-child
            >
              <RouterLink :to="{ name: '/free-demo' }">
                {{ t('how_it_works.cta_banner.action') }}

                <UiIcon
                  name="arrow-forward-rounded"
                  class="transition-transform duration-200 group-hover:translate-x-1"
                />
              </RouterLink>
            </UiButton>
          </div>
        </div>
      </section>
    </section>

    <LayoutFooter />
  </div>
</template>
